import { action, computed, observable } from 'mobx';
import { setter, toggle } from 'mobx-decorators';
import { concatDeserializeMappers, deserializeWithMappers, setFieldValues } from 'stores/Utils';
import OrganizationStore from 'stores/Model/Organization/OrganizationStore';
import BillingDetailsStore from 'stores/Model/Billing/BillingDetailsStore';
import SubscriptionStore from 'stores/Model/Billing/Subscription/SubscriptionStore';
import SubscriptionOfferStore from 'stores/Model/Billing/Subscription/OfferStore';
import AccountStore from 'stores/Model/Account/AccountStore';
import UsageStore from 'stores/Model/Account/UsageStore';
import UsageLimitsStore from 'stores/Model/Account/UsageLimitsStore';
import constants from 'Constants';
import * as auth from 'Auth';
import * as configUtils from 'utils/Config';


class AccountExtendStore extends AccountStore {

  @setter @observable organization;
  @setter @observable billingDetails;
  @setter @observable subscriptions;
  @setter @observable subscriptionOffers;

  @setter @observable numMembers;
  @setter @observable hasStaff;
  @setter @observable hasFailedPayment;
  @setter @observable hasExtendedFailedPayment;

  @setter @observable activeProducts;
  @setter @observable upgradableProducts;
  @setter @observable eligibleProducts;
  @setter @observable isTrialPeriod;
  @setter @observable productsPlanTierIndex;
  @setter @observable usage;
  @setter @observable usageLimits;

  static create(data={}) {
    const instance = new AccountExtendStore();
    setFieldValues(instance, data);
    return instance;
  }

  static getDefaults() {
    return Object.assign({}, AccountStore.getDefaults(), {
      "organization": new OrganizationStore(),
      "billingDetails": new BillingDetailsStore(),
      "subscriptions": [],
      "subscriptionOffers": [],

      "numMembers": 0,
      "hasStaff": false,
      "hasFailedPayment": false,
      "hasExtendedFailedPayment": false,

      "activeProducts": [],
      "upgradableProducts": [],
      "eligibleProducts": [],
      "isTrialPeriod": false,
      "productsPlanTierIndex": {},
      "usage": new UsageStore(),
      "usageLimits": new UsageLimitsStore(),
    });
  }

  constructor(defaults=AccountExtendStore.getDefaults()) {
    super(defaults);
  }

  reset(defaults=AccountExtendStore.getDefaults()) {
    super.reset(defaults);
  }

  getDeserializeMappers() {
    let mappers = [
      {"field": "organization", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": OrganizationStore},
      {"field": "billingDetails", "accessor": "billing_details", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": BillingDetailsStore},
      {"field": "subscriptions", "type": constants.DESERIALIZE_TYPE_ARRAY_OBJECTS, "cls": SubscriptionStore},
      {"field": "subscriptionOffers", "accessor": "subscription_offers", "type": constants.DESERIALIZE_TYPE_ARRAY_OBJECTS, "cls": SubscriptionOfferStore},

      {"field": "numMembers", "accessor": "num_members"},
      {"field": "hasStaff", "accessor": "has_staff"},
      {"field": "hasFailedPayment", "accessor": "has_failed_payment"},
      {"field": "hasExtendedFailedPayment", "accessor": "has_extended_failed_payment"},

      {"field": "activeProducts", "accessor": "active_products"},
      {"field": "upgradableProducts", "accessor": "upgradable_products"},
      {"field": "eligibleProducts", "accessor": "eligible_products"},
      {"field": "isTrialPeriod", "accessor": "is_trial_period"},
      {"field": "productsPlanTierIndex", "accessor": "products_plan_tier_index"},
      {"field": "usage", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": UsageStore},
      {"field": "usageLimits", "accessor": "usage_limits", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": UsageLimitsStore},
    ];

    return concatDeserializeMappers(super.getDeserializeMappers(), mappers);
  }

  @action deserialize(data) {
    deserializeWithMappers(this, this.getDeserializeMappers(), data);
  }

  @computed get hasSubscription() {
    return this.subscriptions.length > 0;
  }

  @computed get hasAllInOneSubscription() {
    return this.subscriptions.some((subscription) => subscription.isAllInOnePlans);
  }

  @computed get activeSubscriptions() {
    return this.subscriptions.filter((subscription) => subscription.isActive);
  }

  @computed get isUsingTrial() {
    if (this.hasSubscription) return false;
    if (!this.isTrialPeriod) return false;
    return true;
  }

  @computed get eligibleReviewRequest() {
    if (this.organization.disableReviewRequest) return false;
    // Uncomment if you only want to display to paid users
    // if (!this.hasSubscription && !this.isTrialPeriod) return false;
    return true;
  }

  getProductDetails(productValue) {
    return constants.PRODUCT_DETAILS.filter((row) => row.value === productValue)[0];
  }

  getSourceDetails(sourceId) {
    return constants.SOURCE_DETAILS[sourceId];
  }

  checkEligiblePlanTier(product, requiredTier) {
    let requiredTierIndex = configUtils.getPlanTierIndex(requiredTier);
    let currentIndex = this.productsPlanTierIndex[product] || 0;
    return currentIndex >= requiredTierIndex;
  }

  checkIsFreePlan(product) {
    let freeTierIndex = configUtils.getPlanTierIndex(constants.PLAN_TIER_FREE);
    let currentIndex = this.productsPlanTierIndex[product] || 0;
    if (freeTierIndex >= currentIndex) return true;
    return false;
  }

  checkPromptProductInstall(productValue) {
    if (this.isEcommercePlatformOthers) return false;
    if (this.activeProducts.includes(productValue)) return false;

    const sourceDetails = this.getSourceDetails(this.ecommercePlatform);
    if (!sourceDetails) return false;

    if (!sourceDetails.eligible_products.includes(productValue)) return false;
    return true;
  }

  @computed get promptMarketingPlatformInstall() {
    return this.checkPromptProductInstall(constants.PRODUCT_MARKETING_PLATFORM);
  }

  @computed get installMarketingPlatformUrl() {
    const productDetails = this.getProductDetails(constants.PRODUCT_MARKETING_PLATFORM);
    return productDetails.app_listings[this.ecommercePlatform];
  }

  @computed get promptAovProgressBarInstall() {
    return this.checkPromptProductInstall(constants.PRODUCT_AOV_PROGRESS_BAR);
  }

  @computed get installAovProgressBarUrl() {
    const productDetails = this.getProductDetails(constants.PRODUCT_AOV_PROGRESS_BAR);
    return productDetails.app_listings[this.ecommercePlatform];
  }

  @computed get promptBackInStockInstall() {
    return this.checkPromptProductInstall(constants.PRODUCT_BACK_IN_STOCK);
  }

  @computed get installBackInStockUrl() {
    const productDetails = this.getProductDetails(constants.PRODUCT_BACK_IN_STOCK);
    return productDetails.app_listings[this.ecommercePlatform];
  }

  @computed get promptReviewsInstall() {
    return this.checkPromptProductInstall(constants.PRODUCT_REVIEWS);
  }

  @computed get installReviewsUrl() {
    const productDetails = this.getProductDetails(constants.PRODUCT_REVIEWS);
    return productDetails.app_listings[this.ecommercePlatform];
  }

  @computed get promptRecommenderInstall() {
    return this.checkPromptProductInstall(constants.PRODUCT_RECOMMENDER);
  }

  @computed get installRecommenderUrl() {
    const productDetails = this.getProductDetails(constants.PRODUCT_RECOMMENDER);
    return productDetails.app_listings[this.ecommercePlatform];
  }

  @computed get promptReperksInstall() {
    return this.checkPromptProductInstall(constants.PRODUCT_REPERKS);
  }

  @computed get installReperksUrl() {
    const productDetails = this.getProductDetails(constants.PRODUCT_REPERKS);
    return productDetails.app_listings[this.ecommercePlatform];
  }

  @computed get promptAppInstall() {
    return this.checkPromptProductInstall(constants.PRODUCT_PROMPT);
  }

  @computed get installPromptUrl() {
    const productDetails = this.getProductDetails(constants.PRODUCT_PROMPT);
    return productDetails.app_listings[this.ecommercePlatform];
  }

  @computed get setupGuideUrl() {
    let defaultUrl = "https://help.onvoard.com/articles/art_3556319276291/guide-for-other-platforms-marketing-platform";
    const sourceDetails = this.getSourceDetails(this.ecommercePlatform);
    if (!sourceDetails || !sourceDetails.help_url) return defaultUrl;
    return sourceDetails.help_url;
  }

  @computed get activeProductsDisplay() {
    const labels = [];
    this.activeProducts.forEach((value) => {
      const option = constants.PRODUCT_OPTIONS.filter((option) => option.value === value)[0];
      if (option) labels.push(option.label);
    });

    return labels;
  }

  @computed get chargableSubscriptions() {
    return this.subscriptions.filter((subscription) => subscription.isChargable);
  }

  @computed get hasGoldV1Subscription() {
    return this.subscriptions.some((subscription) => subscription.plan === constants.GOLD_PLAN_ALL_IN_ONE);
  }

  @computed get hasGoldV2Subscription() {
    let v2Plans = constants.GOLD_PLAN_V2_OPTIONS.map((option) => option.value);
    return this.subscriptions.some((subscription) => v2Plans.includes(subscription.plan));
  }

  @computed get hasJadeSubscription() {
    let plans = constants.JADE_PLAN_OPTIONS.map((option) => option.value);
    return this.subscriptions.some((subscription) => plans.includes(subscription.plan));
  }

  @computed get hasDiamondSubscription() {
    let plans = constants.DIAMOND_PLAN_OPTIONS.map((option) => option.value);
    return this.subscriptions.some((subscription) => plans.includes(subscription.plan));
  }

  @computed get writeReviewUrl() {
    const currentProduct = auth.getCurrentProduct();

    if (this.isEcommercePlatformShopify) return `https://www.onvoard.com/write-shopify-review-for-onvoard-apps`;
    if (this.isEcommercePlatformBigCommerce) return `https://www.onvoard.com/write-bigcommerce-review-for-onvoard-apps`;
    if (this.isEcommercePlatformWix) return `https://www.onvoard.com/write-wix-review-for-onvoard-apps`;
    return `https://www.g2.com/products/onvoard/reviews`;
  }

  @computed get displayUsageItems() {
    if (this.displayMarketingPlatformUsageItems) return true;
    if (this.displayAovProgressBarUsageItems) return true;
    if (this.displayBackInStockUsageItems) return true;
    if (this.displayRecommenderUsageItems) return true;
    if (this.displayReviewsUsageItems) return true;
    if (this.displayReperksUsageItems) return true;
    if (this.displayPromptUsageItems) return true;
    return false;
  }

  @computed get displayMarketingPlatformUsageItems() {
    const usageLimits = this.usageLimits;
    if (usageLimits.marketingPlatformContactsLimit) return true;
    return false;
  }

  @computed get displayAovProgressBarUsageItems() {
    const usageLimits = this.usageLimits;
    if (usageLimits.aovProgressBarPageviewsLimit) return true;
    return false;
  }

  @computed get displayBackInStockUsageItems() {
    const usageLimits = this.usageLimits;
    if (usageLimits.backInStockOverallEmailSendsLimit) return true;
    return false;
  }

  @computed get displayRecommenderUsageItems() {
    const usageLimits = this.usageLimits;
    if (usageLimits.recommenderPageviewsLimit) return true;
    return false;
  }

  @computed get displayReviewsUsageItems() {
    const usageLimits = this.usageLimits;
    if (usageLimits.reviewsOrdersLimit) return true;
    return false;
  }

  @computed get displayReperksUsageItems() {
    const usageLimits = this.usageLimits;
    if (usageLimits.reperksOrdersLimit) return true;
    return false;
  }

  @computed get displayPromptUsageItems() {
    const usageLimits = this.usageLimits;
    if (usageLimits.promptPopupViewsLimit) return true;
    return false;
  }
}


export default AccountExtendStore;
